import React, { PropsWithChildren, useState, useMemo } from 'react';
import { CSSValueWithLength, getZIndex } from '../../styles';
import { InputSize } from '../input/types';
import { Popover } from '../popover';
import { DropdownChipLabel } from '../dropdown';
import { DropdownLabel, DropdownLabelProps } from '../dropdown/DropdownLabel';
import { IconCircleXFill } from '../icons/generated';
import { semantic_colors } from '../../foundation';
import { Stack } from '../stack';
import { RemoveButton } from '../date-picker/DatePickerIcon';
import { CategoryEntry } from './types';
import { generateOptionsFromKeys } from './utils';

export interface ChipsDropdownOption<OptionValue> {
  label: string;
  value: OptionValue;
}

export interface CategorySelectorDropdownProps<OptionValue> {
  /**
   * Input 너비를 지정할 수 있습니다.
   * @default 300
   */
  width?: CSSValueWithLength;
  /** @default getZIndex('dropdown') */
  zIndex?: number;
  /** @default 'medium' */
  size?: InputSize;
  disabled?: boolean;
  /**
   * @deprecated 레거시 categoriesByLevels props 사용시에만 사용해주세요.
   */
  selectedOptions?: Array<ChipsDropdownOption<OptionValue>>;
  /**
   *
   * multiple true일 경우에만 optionChip 변경 이벤트를 전달합니다.
   */
  onOptionValuesChange?: (values: Array<OptionValue>) => void;
  placeholder?: string;
  status?: DropdownLabelProps['status'];
  /**
   * Popover 바깥 영역 클릭시 팝업 닫기 여부
   * @default false
   * */
  canClickOutside?: boolean;
  /**
   * multiple 모드 여부, true일 경우 DropdownChipLabel 사용, false일 경우 DropdownLabel 사용
   * @default false
   */
  multiple?: boolean;
}

interface ChildrenProps {
  multiple?: boolean;
  onSelectedKeysChange?: (selectedKeys: Array<string>, isLeaf?: boolean) => void;
  opened?: boolean;
  items?: CategoryEntry[];
  selectedKeys?: Array<string>;
}

export const CategorySelectorDropdown = <OptionValue extends string | number>({
  children,
  width = 300,
  zIndex = getZIndex('dropdown'),
  disabled,
  selectedOptions,
  onOptionValuesChange,
  placeholder,
  status,
  size: sizeProp = 'medium',
  canClickOutside = false,
  multiple: multipleProp,
}: PropsWithChildren<CategorySelectorDropdownProps<OptionValue>>) => {
  const [opened, setOpened] = useState(false);
  const [showRemoveIcon, setShowRemoveIcon] = useState(false);

  const childrenItems = useMemo(
    () =>
      React.isValidElement<ChildrenProps>(children) && 'items' in children.props ? children.props.items : undefined,
    [children],
  );
  const childrenSelectedKeys = useMemo(
    () =>
      React.isValidElement<ChildrenProps>(children) && 'selectedKeys' in children.props
        ? children.props.selectedKeys
        : [],
    [children],
  );
  const childrenMultiple =
    React.isValidElement<ChildrenProps>(children) && 'multiple' in children.props ? children.props.multiple : undefined;
  const multiple = multipleProp ?? childrenMultiple ?? false;

  const computedSelectedOptions = useMemo(() => {
    if (selectedOptions) {
      return selectedOptions;
    }
    if (childrenItems && childrenSelectedKeys && childrenSelectedKeys.length > 0) {
      return generateOptionsFromKeys(childrenItems, childrenSelectedKeys);
    }
    return [];
  }, [selectedOptions, childrenItems, childrenSelectedKeys]);

  const isSelectedLeaf = !multipleProp && computedSelectedOptions.length > 0;

  const handleOpen = () => {
    setOpened(true);
  };

  const handleClose = () => {
    setOpened(false);
  };

  const handleSelectedKeysChange = (selectedKeys: Array<string>, isLeaf?: boolean) => {
    if (!multiple && isLeaf) {
      setOpened(false);
    }
    if (React.isValidElement<ChildrenProps>(children) && children.props.onSelectedKeysChange) {
      children.props.onSelectedKeysChange(selectedKeys, isLeaf);
    }
  };

  const renderChipInput = () => {
    const commonProps = {
      expanded: opened,
      width: width,
      placeholder: placeholder,
      status: status,
      size: sizeProp,
    };

    if (multiple) {
      return (
        <DropdownChipLabel
          {...commonProps}
          selectedOptions={computedSelectedOptions}
          // @ts-expect-error: TS2322
          onOptionValuesChange={onOptionValuesChange}
        />
      );
    }
    const selectedLabel = computedSelectedOptions.length > 0 ? computedSelectedOptions[0].label : placeholder;
    return (
      <DropdownLabel
        {...commonProps}
        selected={computedSelectedOptions.length > 0}
        onMouseEnter={() => isSelectedLeaf && setShowRemoveIcon(true)}
        onMouseLeave={() => isSelectedLeaf && setShowRemoveIcon(false)}
      >
        <Stack direction='row' align='center' justify='space-between' width='100%'>
          {selectedLabel}
          {showRemoveIcon && (
            <RemoveButton
              color={semantic_colors.content.tertiary}
              onClick={(e) => {
                e.stopPropagation();
                handleSelectedKeysChange([], false);
                setShowRemoveIcon(false);
              }}
            >
              <IconCircleXFill size={16} color={semantic_colors.content.tertiary} />
            </RemoveButton>
          )}
        </Stack>
      </DropdownLabel>
    );
  };

  const clonedChildren = React.isValidElement<ChildrenProps>(children)
    ? React.cloneElement(children, {
        ...children.props,
        onSelectedKeysChange: handleSelectedKeysChange,
        multiple,
        opened,
      })
    : children;

  return disabled ? (
    renderChipInput()
  ) : (
    <Popover
      arrow={false}
      zIndex={zIndex}
      canClickOutside={canClickOutside}
      padding={0}
      content={clonedChildren}
      opened={opened}
      onOpen={handleOpen}
      onClose={handleClose}
    >
      {renderChipInput()}
    </Popover>
  );
};
